import React, { FunctionComponent, ReactNode } from 'react'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'

import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface AuroraAwareBackgroundProps {
  children: ReactNode
  fallbackBackground?: {
    component: React.ElementType
    props?: Record<string, any>
  }
  /** When true, the gradient is contained within its parent (position: absolute) rather than fixed to the viewport. */
  contained?: boolean
  /** When true, hides the gradient background while keeping the wrapper structure. */
  hideGradient?: boolean
  /** When true, removes height constraints to allow content to wrap at its natural height instead of stretching to fill the parent container. */
  allowContentWrap?: boolean
}

const BackgroundContainer = styled.View<{ allowContentWrap?: boolean }>(({ allowContentWrap }) => ({
  ...(!allowContentWrap && { height: '100%' }),
  position: 'relative',
}))

const FixedGradient = styled(LinearGradient)<{ contained?: boolean }>(({ contained }) => ({
  top: 0,
  left: 0,
  right: 0,
  ...(IS_WEB && !contained
    ? {
        position: 'fixed',
        height: '100vh',
      }
    : {
        position: 'absolute',
        bottom: 0,
      }),
}))

const ContentWrapper = styled.View<{ allowContentWrap?: boolean }>(({ allowContentWrap }) => ({
  ...(!allowContentWrap && { flexGrow: 1, height: '100%' }),
}))

/**
 * A wrapper component that applies an aurora-themed background gradient when the aurora theme is active,
 * and falls back to a provided background component or no background when not.
 *
 * On web, the gradient is fixed to the viewport (position: fixed) and remains static as you scroll.
 * On mobile, the gradient stretches with the content (position: absolute).
 */
export const AuroraAwareBackground: FunctionComponent<AuroraAwareBackgroundProps> = ({
  children,
  fallbackBackground,
  contained,
  hideGradient,
  allowContentWrap,
}) => {
  const {
    colors: { backgroundGradientFirst, backgroundGradientSecond },
    isAuroraTheme,
  } = useTheme() as ThemeType

  const gradientColors = [backgroundGradientFirst, backgroundGradientSecond] as [string, string]
  const FallbackComponent = !isAuroraTheme ? fallbackBackground?.component : undefined
  const Wrapper = FallbackComponent || BackgroundContainer
  const wrapperProps = FallbackComponent ? fallbackBackground?.props : { allowContentWrap }

  return (
    <Wrapper {...wrapperProps}>
      {isAuroraTheme && !hideGradient && (
        <FixedGradient
          testID={tID('AuroraGradientBackground')}
          contained={contained}
          colors={gradientColors}
          start={{ x: 0.5, y: 0 }}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 1]}
        />
      )}
      <ContentWrapper allowContentWrap={allowContentWrap} testID={tID('AuroraAwareBackgroundWrapper')}>
        {children}
      </ContentWrapper>
    </Wrapper>
  )
}
